/**
 * Ozalo — boosters du tiroir panier (thème Horizon).
 *
 * 1. <ozalo-cart-timer>  : compte à rebours « panier réservé », partagé entre les pages
 *    (localStorage). À 0, il repart discrètement pour une nouvelle période.
 * 2. <ozalo-cart-upsell> : bouton « Ajouter » des produits proposés. Passe par la même
 *    mécanique que le bouton panier du thème (CartLinesUpdateEvent + sections) pour que le
 *    tiroir, la pastille du panier et la barre de paliers se mettent à jour.
 * 3. Cadeaux de palier : ajoute / retire automatiquement le produit cadeau quand le montant
 *    franchit le palier (propriété cachée _ozalo_cadeau). Le prix à 0 € vient d'une
 *    réduction automatique Shopify à créer sur ce produit.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const TIMER_KEY = 'ozaloCartTimerEnd';
const SOURCE = 'ozalo-cart-boost';

/* ------------------------------------------------------------------ */
/* Utilitaires                                                         */
/* ------------------------------------------------------------------ */

function storageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* navigation privée : le minuteur repart simplement à chaque page */
  }
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Panier indisponible (${response.status})`);
  return response.json();
}

/**
 * Envoie une modification de panier (/cart/add.js ou /cart/update.js) en demandant le HTML
 * des sections panier, puis prévient le thème pour qu'il redessine le tiroir.
 *
 * @param {HTMLElement} origin - élément qui émet l'événement (doit être dans la page)
 * @param {'add'|'update'} kind
 * @param {object} payload
 * @param {Array<{merchandiseId?: string, id?: string, quantity: number}>} lines
 */
async function mutateCart(origin, kind, payload, lines) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: kind === 'add' ? 'add' : 'update',
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    const routes = window.Theme?.routes || {};
    const url = kind === 'add' ? routes.cart_add_url || '/cart/add' : routes.cart_update_url || '/cart/update';
    const response = await fetch(url.endsWith('.js') ? url : `${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        ...payload,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: SOURCE, didError: true },
      });
      throw new Error(data.description || data.message || 'Ajout impossible');
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: data.sections,
        source: SOURCE,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject?.(error);
    throw error;
  }
}

/* ------------------------------------------------------------------ */
/* 1. Minuteur « panier réservé »                                     */
/* ------------------------------------------------------------------ */

class OzaloCartTimer extends HTMLElement {
  #interval = 0;

  connectedCallback() {
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
  }

  get #durationMs() {
    const minutes = parseInt(this.dataset.minutes || '15', 10);
    return Math.max(1, minutes) * 60 * 1000;
  }

  #endTime() {
    const now = Date.now();
    let end = parseInt(storageGet(TIMER_KEY) || '0', 10);
    if (!end || end <= now || end - now > this.#durationMs) {
      end = now + this.#durationMs;
      storageSet(TIMER_KEY, String(end));
    }
    return end;
  }

  #tick() {
    const remaining = Math.max(0, this.#endTime() - Date.now());
    const totalSeconds = Math.ceil(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const target = this.querySelector('[data-ozb-time]');
    if (target) target.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
}

if (!customElements.get('ozalo-cart-timer')) {
  customElements.define('ozalo-cart-timer', OzaloCartTimer);
}

/* ------------------------------------------------------------------ */
/* 2. Produits proposés                                               */
/* ------------------------------------------------------------------ */

class OzaloCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ozb-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    const card = button.closest('.ozb-card');
    const input = card?.querySelector('[data-ozb-variant]');
    const variantId = input && 'value' in input ? String(input.value) : '';
    if (!variantId) return;

    const errorBox = this.querySelector('[data-ozb-error]');
    if (errorBox) errorBox.hidden = true;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      await mutateCart(button, 'add', { items: [{ id: Number(variantId), quantity: 1 }] }, [
        { merchandiseId: variantId, quantity: 1 },
      ]);
      // Le tiroir est redessiné par le thème : la carte disparaît (produit déjà au panier).
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = error instanceof Error ? error.message : 'Ajout impossible';
        errorBox.hidden = false;
      }
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  };
}

if (!customElements.get('ozalo-cart-upsell')) {
  customElements.define('ozalo-cart-upsell', OzaloCartUpsell);
}

/* ------------------------------------------------------------------ */
/* 3. Cadeaux de palier (ajout / retrait automatique)                 */
/* ------------------------------------------------------------------ */

let giftSyncRunning = false;

function readGiftConfig() {
  const node = document.querySelector('[data-ozb-gifts]');
  if (!node) return [];
  try {
    const parsed = JSON.parse(node.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function isGiftLine(item) {
  return Boolean(item?.properties && item.properties._ozalo_cadeau);
}

async function syncGifts() {
  if (giftSyncRunning) return;
  const gifts = readGiftConfig();
  // Aucun cadeau configuré et aucune ligne cadeau affichée : rien à faire, pas d'appel réseau
  if (!gifts.length && !document.querySelector('[data-ozb-gifts]')) return;

  giftSyncRunning = true;
  try {
    const cart = await fetchCart();
    const giftLines = cart.items.filter(isGiftLine);
    if (!gifts.length && !giftLines.length) return;

    const giftValue = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
    const eligible = Math.max(0, cart.total_price - giftValue);
    const paidCount = cart.items.length - giftLines.length;

    const wanted = new Set(
      paidCount > 0 ? gifts.filter((g) => eligible >= g.threshold).map((g) => String(g.variantId)) : []
    );

    // Lignes cadeau à retirer (palier perdu, cadeau retiré des réglages, ou quantité > 1)
    /** @type {Record<string, number>} */
    const updates = {};
    const present = new Set();
    for (const item of giftLines) {
      const id = String(item.variant_id);
      if (!wanted.has(id) || present.has(id)) {
        updates[item.key] = 0;
      } else {
        present.add(id);
        if (item.quantity !== 1) updates[item.key] = 1;
      }
    }

    const toAdd = [...wanted].filter((id) => !present.has(id));
    const origin = document.querySelector('cart-drawer-component') || document.body;

    if (Object.keys(updates).length) {
      await mutateCart(
        origin,
        'update',
        { updates },
        Object.keys(updates).map((key) => ({ id: key, quantity: updates[key] }))
      );
    }

    if (toAdd.length) {
      await mutateCart(
        origin,
        'add',
        { items: toAdd.map((id) => ({ id: Number(id), quantity: 1, properties: { _ozalo_cadeau: 'Offert' } })) },
        toAdd.map((id) => ({ merchandiseId: id, quantity: 1 }))
      );
    }
  } catch (error) {
    console.warn('[ozalo-cart-boost] cadeaux :', error);
  } finally {
    giftSyncRunning = false;
  }
}

if (!window.__ozaloCartBoostListening) {
  window.__ozaloCartBoostListening = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then((result) => {
        if (result?.detail?.source === SOURCE || result?.detail?.didError) return;
        // laisse le thème redessiner le tiroir (la config cadeaux est dans ce HTML)
        requestAnimationFrame(() => syncGifts());
      })
      .catch(() => {});
  });

  // Au chargement : panier déjà au-dessus d'un palier, ou palier modifié depuis
  syncGifts();
}
